import { useCallback, useEffect, useRef, useState } from 'react';
import { usePlaidLink } from 'react-plaid-link';
import {
  isPendingBankPayment,
  useConnectPlaid,
  useFinalizePlaidVerify,
  useGetPaymentMethods,
  useMintConnectLinkToken,
  useMintVerifyLinkToken,
  useSelectPaymentMethod,
} from '~/domain/Payments';
import { CustomPortal } from '../../../commonComponents';
import { ShowAchAuthorizationPopup } from '../../../constants/ff.posthog';
import { resetInviteProfessionals } from '../../../domain/utils';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag';
import { Button } from '@tempmee/react';
import { OutlineLibraryIcon as LibraryIcon } from '@tempmee/react-icons';
import { AchAuthorizationPopup } from '../../AchAuthorizationPopup/AchAuthorizationPopup';
import { useModal } from '../../ModalQueue';
import { ModalOrder } from '../../ModalQueue/modalOrder';
import { PaymentCard } from './PaymentCard';

const PENDING_MANUAL_VERIFICATION_STATUS = 'pending_manual_verification';
const MAX_PENDING_BANKS = 3;

const isPendingManualVerificationMetadata = (metadata) =>
  metadata?.account?.verification_status === PENDING_MANUAL_VERIFICATION_STATUS ||
  metadata?.accounts?.some(
    (account) => account?.verification_status === PENDING_MANUAL_VERIFICATION_STATUS,
  );

const getVerifiedBankAccounts = (payments = []) =>
  payments.filter((payment) => payment.type === 'bank_account' && !isPendingBankPayment(payment));

export const BankList = ({ selectedPayment, setSelectedPayment }) => {
  const { paymentMethods: allPayments, refetch: refetchPaymentMethods } = useGetPaymentMethods();
  const mintConnectLinkToken = useMintConnectLinkToken();
  const mintVerifyLinkToken = useMintVerifyLinkToken();
  const connectPlaid = useConnectPlaid();
  const finalizePlaidVerify = useFinalizePlaidVerify();
  const selectPaymentMethod = useSelectPaymentMethod();
  const [showSuccessBankAddedPopup, setShowSuccessBankAddedPopup] = useState(false);
  const [showVerificationFailedPopup, setShowVerificationFailedPopup] = useState(false);
  const [showAchPopup, setShowAchPopup] = useState(false);
  const [pendingPlaidData, setPendingPlaidData] = useState(null);
  const [pendingOpen, setPendingOpen] = useState(false);
  const [pendingVerificationCandidateId, setPendingVerificationCandidateId] = useState(null);
  const activeVerificationCandidateIdRef = useRef(null);
  const verificationSnapshotRef = useRef(null);

  const isAchPopupEnabled = useFeatureFlag(ShowAchAuthorizationPopup);

  const { isActive: isSuccessPopupActive } = useModal({
    id: ModalOrder.BANK_SUCCESS_POPUP,
    condition: showSuccessBankAddedPopup,
  });

  const submitPlaidData = (publicToken, metadata, achMandateAuthorized = false) => {
    connectPlaid.mutate(
      { metadata, publicToken, ...(achMandateAuthorized && { achMandateAuthorized }) },
      {
        onSuccess: () => {
          if (!isPendingManualVerificationMetadata(metadata)) {
            setShowSuccessBankAddedPopup(true);
          }
        },
      },
    );
    resetInviteProfessionals();
  };

  const { open, ready } = usePlaidLink({
    token: mintConnectLinkToken.data?.token,
    onSuccess: (publicToken, metadata) => {
      if (isAchPopupEnabled) {
        setPendingPlaidData({ publicToken, metadata });
        setShowAchPopup(true);
      } else {
        submitPlaidData(publicToken, metadata);
      }
    },
  });

  const resolveVerificationOutcome = useCallback(
    async (candidateId) => {
      const snapshot = verificationSnapshotRef.current;
      verificationSnapshotRef.current = null;

      const refreshed = await refetchPaymentMethods();
      const refreshedPayments = refreshed.data ?? [];
      const pendingCandidate = refreshedPayments.find((payment) => payment.id === candidateId);

      if (isPendingBankPayment(pendingCandidate)) {
        return;
      }

      const previousVerifiedBankIds = snapshot?.verifiedBankIds ?? new Set();
      const newVerifiedBank = getVerifiedBankAccounts(refreshedPayments).find(
        (payment) => !previousVerifiedBankIds.has(payment.id),
      );

      if (newVerifiedBank) {
        selectPaymentMethod.mutate(newVerifiedBank.id);
        setShowSuccessBankAddedPopup(true);
        return;
      }

      setShowVerificationFailedPopup(true);
    },
    [refetchPaymentMethods, selectPaymentMethod],
  );

  const finalizePendingVerification = useCallback((userCompleted) => {
    const candidateId = activeVerificationCandidateIdRef.current;

    if (!candidateId) {
      return;
    }

    activeVerificationCandidateIdRef.current = null;
    setPendingVerificationCandidateId(null);
    finalizePlaidVerify.mutate(
      {
        candidateId,
        ...(isAchPopupEnabled && { achMandateAuthorized: userCompleted }),
      },
      {
        onSuccess: () => resolveVerificationOutcome(candidateId),
      },
    );
  }, [finalizePlaidVerify, isAchPopupEnabled, resolveVerificationOutcome]);

  const { open: openVerify, ready: verifyReady } = usePlaidLink({
    token: mintVerifyLinkToken.data?.linkToken,
    onSuccess: () => finalizePendingVerification(true),
    onExit: () => finalizePendingVerification(false),
  });

  const handleAddBankAccount = () => {
    mintConnectLinkToken.mutate(undefined, {
      onSuccess: () => setPendingOpen(true),
    });
  };

  const handleAchConfirm = () => {
    setShowAchPopup(false);
    if (pendingPlaidData) {
      submitPlaidData(pendingPlaidData.publicToken, pendingPlaidData.metadata, true);
      setPendingPlaidData(null);
    }
  };

  const handleConfirmDeposit = (candidateId) => {
    activeVerificationCandidateIdRef.current = candidateId;
    verificationSnapshotRef.current = {
      verifiedBankIds: new Set(getVerifiedBankAccounts(allPayments).map((payment) => payment.id)),
    };
    mintVerifyLinkToken.mutate(
      { candidateId },
      {
        onSuccess: () => setPendingVerificationCandidateId(candidateId),
        onError: () => {
          activeVerificationCandidateIdRef.current = null;
          verificationSnapshotRef.current = null;
          setPendingVerificationCandidateId(null);
        },
      },
    );
  };

  const bankList = allPayments
    ?.filter((item) => item.type === 'bank_account')
    .sort((a, b) => (b.is_selected === true) - (a.is_selected === true));
  const pendingBankCount = bankList?.filter(isPendingBankPayment).length ?? 0;

  useEffect(() => {
    if (pendingOpen && ready && mintConnectLinkToken.data?.token) {
      open();
      setPendingOpen(false);
    }
  }, [mintConnectLinkToken.data?.token, open, pendingOpen, ready]);

  useEffect(() => {
    if (pendingVerificationCandidateId && verifyReady && mintVerifyLinkToken.data?.linkToken) {
      openVerify();
      setPendingVerificationCandidateId(null);
    }
  }, [
    mintVerifyLinkToken.data?.linkToken,
    openVerify,
    pendingVerificationCandidateId,
    verifyReady,
  ]);

  const renderPendingVerificationAction = (item) => {
    if (!isPendingBankPayment(item)) {
      return null;
    }

    const isLoading =
      mintVerifyLinkToken.isPending &&
      activeVerificationCandidateIdRef.current === item.id;

    return (
      <Button
        data-cy="btn-confirm-bank-deposit"
        type="button"
        size="xs"
        variant="secondary"
        isLoading={isLoading}
        onClick={(event) => {
          event.stopPropagation();
          handleConfirmDeposit(item.id);
        }}
      >
        Confirm Deposit
      </Button>
    );
  };

  return (
    <>
      <PaymentCard
        dataCy="bank-list-payment"
        data={bankList}
        selectedPayment={selectedPayment}
        setSelectedPayment={setSelectedPayment}
        IconComponent={LibraryIcon}
        title="Bank Account"
        description="(Adding a Bank Account is the preferred method as you avoid the 3.5% Convenience Fee)."
        addPaymentText="Add Bank Account"
        handleAddPaymentButton={handleAddBankAccount}
        addDisabled={pendingBankCount >= MAX_PENDING_BANKS}
        renderLineAction={renderPendingVerificationAction}
      />

      {showAchPopup && <AchAuthorizationPopup onConfirm={handleAchConfirm} />}

      {isSuccessPopupActive && (
        <CustomPortal
          dataCy="bank-list-payment-success-popup"
          isSuccess
          redirect={false}
          title="Bank Account Successfully Added!"
          text="Your bank account information has been successfully added to your account."
          submitPressed={() => {
            setShowSuccessBankAddedPopup(false);
          }}
          rightButtonDataCy="btn-ok"
        />
      )}

      {showVerificationFailedPopup && (
        <CustomPortal
          dataCy="bank-list-payment-verification-failed-popup"
          isError
          redirect={false}
          title="Verification Failed"
          text="Verification failed. Please reconnect your bank account."
          submitPressed={() => {
            setShowVerificationFailedPopup(false);
          }}
          rightButtonDataCy="btn-ok"
        />
      )}
    </>
  );
};
